import React, { useState } from 'react';

import { useDateFormatter } from '@mint-lib/formatting-hooks';
import {
  CalendarRange,
  DatepickerInput,
  type DateRange,
} from '@mint-lib/tw-components/patterns';
import { format } from 'date-fns';
import { useSetAtom } from 'jotai';

import { useSuggestionFilter } from '../../../hooks/useSuggestionFilter.js';
import { selectedSuggestionAtom } from '../../../store/index.js';
import {
  initialSuggestionDateRange,
  SuggestionFilterActions,
} from '../../../store/suggestion/suggestionFilter.js';

const DateFilter = () => {
  const { selectedSuggestionFilter, setSuggestionFilter } =
    useSuggestionFilter();
  const setSuggestion = useSetAtom(selectedSuggestionAtom);

  const resetDateRange = {
    from: new Date(initialSuggestionDateRange.from),
    to: new Date(initialSuggestionDateRange.to),
  } satisfies DateRange;

  const initialDateRange = {
    from: new Date(selectedSuggestionFilter.dateFrom),
    to: new Date(selectedSuggestionFilter.dateTo),
  } satisfies DateRange;

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(
    initialDateRange,
  );

  const { formattedDateRange } = useDateFormatter(
    selectedRange?.from,
    selectedRange?.to,
  );
  const [selectedValue, setSelectedValue] = useState(formattedDateRange);

  const [month, setMonth] = useState(selectedRange?.to);

  const [isOpen, setIsOpen] = useState(false);

  const handleApplyRangeDate = (dateRange?: DateRange) => {
    if (dateRange) {
      const { from, to } = dateRange;

      if (from && to) {
        setSuggestion(null);
        setSuggestionFilter({
          type: SuggestionFilterActions.SET_DATE,
          data: {
            dateFrom: format(from, 'yyyy-MM-dd'),
            dateTo: format(to, 'yyyy-MM-dd'),
          },
        });
        setIsOpen(false);
        setSelectedValue(formattedDateRange);
      }
    }
  };

  const handleSetSelectedRange = (dateRange?: DateRange) => {
    if (dateRange) {
      setSelectedRange(dateRange);
    }
  };

  const onReset = () => {
    setSelectedRange(resetDateRange);
  };

  return (
    <DatepickerInput
      variant="range"
      clearable={false}
      open={isOpen}
      onOpenChange={setIsOpen}
      value={selectedValue}
      popoverClassName="z-mui right-6"
      className="w-80"
    >
      <CalendarRange
        onSelect={handleSetSelectedRange}
        onApply={handleApplyRangeDate}
        selected={{
          from: selectedRange?.from,
          to: selectedRange?.to,
        }}
        month={month}
        onMonthChange={setMonth}
        onReset={onReset}
      />
    </DatepickerInput>
  );
};

export default DateFilter;
